import * as React from 'react';
import {
  DataGridProProps,
  GridColDef,
  GridGroupNode,
  GridRenderCellParams,
  GridValueGetterParams,
  useGridApiContext,
} from '@mui/x-data-grid-pro';
import { Box, IconButton } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { Ad, Campaign, MetricWindow, TestingAd, WindowKey } from './types';
import { DEFAULT_PREMIUM_MAX, DEFAULT_TRUE_TARGET, PRIMARY_WINDOW, fmtMoney } from './utils';
import MetricCell, { ColorAs, MetricKind } from './components/MetricCell';
import VerdictCell from './components/VerdictCell';

// Columns flex to fill the grid width so the table never scrolls horizontally —
// everything is visible at once. minWidth keeps the 4-window micro-row legible;
// only on a very narrow viewport (below the sum of minWidths) would a scrollbar
// appear.
const metricCol = (
  field: string,
  headerName: string,
  metricKey: keyof MetricWindow,
  kind: MetricKind,
  colorAs?: ColorAs,
  opts: { flex?: number; minWidth?: number; sortWindow?: WindowKey } = {},
): GridColDef => ({
  field,
  headerName,
  flex: opts.flex ?? 1,
  minWidth: opts.minWidth ?? 104,
  sortable: true,
  valueGetter: (p: GridValueGetterParams) =>
    p.row?.w?.[opts.sortWindow ?? PRIMARY_WINDOW]?.[metricKey] ?? null,
  renderCell: (p: GridRenderCellParams) => (
    <MetricCell
      windows={p.row.w}
      metricKey={metricKey}
      kind={kind}
      colorAs={colorAs}
      target={p.row.target_true_roas}
      premiumMax={p.row.premium_max}
    />
  ),
});

const ROAS_COLS = (): GridColDef[] => [
  metricCol('true', 'True ROAS', 'true_roas', 'roas', 'true', { flex: 1.15, minWidth: 116 }),
  metricCol('meta', 'Meta', 'meta_roas', 'roas'),
  metricCol('wmlc', 'WM LC', 'wm_last_click', 'roas'),
  metricCol('wminc', 'WM Inc', 'wm_incrementality', 'roas'),
];

// ---- Campaign Overview: campaign → ad set → ad tree (name pinned) ----------
// Rendered with DataGridPro treeData: every row carries a `path` ([campaign],
// [campaign, adset] or [campaign, adset, ad]) and the same metric windows, so
// one set of columns serves all three levels. Campaigns collapsed by default —
// the table reads exactly as before until a row is expanded.

// Name cell with the expand/collapse chevron. Rows without children (e.g.
// snapshots that predate the drill-down) render as plain leaves — no chevron.
function OverviewNameCell(p: GridRenderCellParams) {
  const api = useGridApiContext();
  const node = p.rowNode;
  const group = node.type === 'group' ? (node as GridGroupNode) : null;
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        pl: `${node.depth * 20}px`,
        py: '4px',
        lineHeight: 1.35,
        minWidth: 0,
      }}
    >
      <Box sx={{ width: 24, flexShrink: 0 }}>
        {group && (
          <IconButton
            size="small"
            sx={{ p: '1px', mt: '-1px' }}
            aria-label={group.childrenExpanded ? 'collapse' : 'expand'}
            onClick={e => {
              e.stopPropagation();
              api.current.setRowChildrenExpansion(p.id, !group.childrenExpanded);
            }}
          >
            {group.childrenExpanded ? (
              <KeyboardArrowDownIcon sx={{ fontSize: 16 }} />
            ) : (
              <KeyboardArrowRightIcon sx={{ fontSize: 16 }} />
            )}
          </IconButton>
        )}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Box
          sx={{
            fontWeight: p.row.level === 'ad' ? 400 : 500,
            fontSize: 12,
            color: 'text.primary',
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            // Fixed row heights in tree mode — clamp long names to two lines.
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {p.row.name}
          {p.row.paused && (
            <Box
              component="span"
              sx={{ ml: '6px', fontSize: 10, color: 'text.disabled', textTransform: 'uppercase' }}
            >
              paused
            </Box>
          )}
        </Box>
        {p.row.subtitle && (
          <Box sx={{ fontSize: 11, color: 'text.secondary', mt: '2px' }}>{p.row.subtitle}</Box>
        )}
      </Box>
    </Box>
  );
}

export function buildCampaignTable(campaigns: Campaign[]) {
  const columns: GridColDef[] = [
    metricCol('spend', 'Spend', 'spend', 'money', undefined, {
      flex: 1.3,
      minWidth: 128,
      sortWindow: 'today',
    }),
    ...ROAS_COLS(),
  ];
  const groupingColDef: DataGridProProps['groupingColDef'] = {
    headerName: 'Campaign',
    flex: 2.2,
    minWidth: 260,
    sortable: true,
    valueGetter: (p: GridValueGetterParams) => p.row.name,
    renderCell: (p: GridRenderCellParams) => <OverviewNameCell {...p} />,
  };
  const rows: Record<string, unknown>[] = [];
  campaigns.forEach((c, i) => {
    const cid = c.campaign_id || `${c.campaign_name}-${i}`;
    // Children inherit the campaign's target so ROAS cells color consistently.
    const shared = {
      campaign_name: c.campaign_name,
      product: c.product || '',
      target_true_roas: c.target_true_roas ?? DEFAULT_TRUE_TARGET,
      premium_max: DEFAULT_PREMIUM_MAX,
    };
    rows.push({
      id: cid,
      path: [cid],
      level: 'campaign',
      name: c.campaign_name,
      subtitle: c.product || '',
      ...shared,
      w: c.windows,
    });
    (c.adsets ?? []).forEach(s => {
      const sid = `${cid}:${s.adset_id}`;
      rows.push({ id: sid, path: [cid, sid], level: 'adset', name: s.adset_name, ...shared, w: s.windows });
      (s.ads ?? []).forEach(a => {
        rows.push({
          id: `${sid}:${a.ad_id}`,
          path: [cid, sid, `${sid}:${a.ad_id}`],
          level: 'ad',
          name: a.ad_name,
          paused: !!a.status && a.status !== 'ACTIVE',
          ...shared,
          w: a.windows,
        });
      });
    });
  });
  const getTreeDataPath: DataGridProProps['getTreeDataPath'] = row => row.path as string[];
  return { rows, columns, groupingColDef, getTreeDataPath };
}

// ---- Ad tables (winners / decisions): verdict pinned + every metric --------
export function buildAdTable(ads: Ad[]) {
  const columns: GridColDef[] = [
    {
      field: 'entity',
      headerName: 'Verdict · Ad · Campaign',
      flex: 2.6,
      minWidth: 260,
      sortable: true,
      valueGetter: (p: GridValueGetterParams) => p.row.ad_name,
      renderCell: (p: GridRenderCellParams) => (
        <VerdictCell
          verdict={p.row.verdict}
          adName={p.row.ad_name}
          adSuffix={p.row.ad_suffix}
          campaignName={p.row.campaign_name}
          rationale={p.row.rationale}
          rationaleDetail={p.row.rationale_detail}
        />
      ),
    },
    metricCol('spend', 'Spend', 'spend', 'money', undefined, { flex: 1.3, minWidth: 128 }),
    metricCol('premium', 'Premium', 'premium', 'premium', 'premium'),
    ...ROAS_COLS(),
  ];
  const rows = ads.map((a, i) => ({
    id: a.ad_id || `${a.ad_name}-${i}`,
    ad_name: a.ad_name,
    ad_suffix: adSuffix(ads, a),
    campaign_name: a.campaign_name,
    product: a.product || '',
    verdict: a.verdict || '',
    rationale: a.rationale || '',
    rationale_detail: a.rationale_detail || '',
    target_true_roas: a.targets?.true_roas ?? DEFAULT_TRUE_TARGET,
    premium_max: a.targets?.premium_max ?? DEFAULT_PREMIUM_MAX,
    w: a.windows,
  }));
  return { rows, columns };
}

// Duplicated creatives can share one ad name (e.g. two "First Class" ads in the
// same campaign) — the numbers are per-ad and correct, but identical labels read
// as one ad. When a name repeats in the table, append an ad_id tail so each row
// is visibly a distinct ad.
function adSuffix(
  all: { ad_name: string; ad_id?: string }[],
  a: { ad_name: string; ad_id?: string },
) {
  const dupes = all.filter(x => x.ad_name === a.ad_name).length > 1;
  if (!dupes || !a.ad_id) return '';
  // Meta ad ids in one account share a trailing chunk — the distinguishing
  // digits sit just before the last four.
  return `(…${String(a.ad_id).slice(-8, -4)})`;
}

// ---- Testing ads past 3x CPA -----------------------------------------------
export function buildTestingTable(rows: TestingAd[]) {
  const columns: GridColDef[] = [
    {
      field: 'entity',
      headerName: 'Ad · Campaign',
      flex: 2.4,
      minWidth: 240,
      sortable: true,
      valueGetter: (p: GridValueGetterParams) => p.row.ad_name,
      renderCell: (p: GridRenderCellParams) => (
        <VerdictCell
          adName={p.row.ad_name}
          adSuffix={p.row.ad_suffix}
          campaignName={p.row.campaign_name}
        />
      ),
    },
    { field: 'lifetime_spend', headerName: 'Lifetime $', flex: 1, minWidth: 108, type: 'number', valueFormatter: (p: { value: unknown }) => fmtMoney(p.value) },
    { field: 'lifetime_cpa', headerName: 'Lifetime CPA', flex: 1, minWidth: 112, type: 'number', valueFormatter: (p: { value: unknown }) => fmtMoney(p.value) },
    { field: 'cpa_target', headerName: 'CPA target', flex: 1, minWidth: 104, type: 'number', valueFormatter: (p: { value: unknown }) => fmtMoney(p.value) },
    {
      field: 'multiple_of_cpa',
      headerName: '× CPA',
      flex: 0.8,
      minWidth: 84,
      type: 'number',
      renderCell: (p: GridRenderCellParams) => {
        const v = p.value as number | null;
        const bad = typeof v === 'number' && v >= 3;
        return (
          <Box
            component="span"
            sx={{
              px: '8px',
              py: '2px',
              borderRadius: '6px',
              fontWeight: 500,
              backgroundColor: bad ? 'var(--color-background-danger, #fdecec)' : 'transparent',
              color: bad ? 'var(--color-text-danger, #c0392b)' : 'inherit',
            }}
          >
            {typeof v === 'number' ? `${v.toFixed(1)}×` : '–'}
          </Box>
        );
      },
    },
    { field: 'verdict', headerName: 'Verdict', flex: 1.6, minWidth: 150 },
  ];
  const mapped = rows.map((r, i) => ({
    id: r.ad_id || `${r.ad_name}-${i}`,
    ad_suffix: adSuffix(rows, r),
    ...r,
  }));
  return { rows: mapped, columns };
}

export const SPEND_SORT = [{ field: 'spend', sort: 'desc' as const }];
